// A tinted aside for prose: a tip (blue), a note (neutral), or a warning
// (amber), with a heroicon and an optional title. The tint pairs are the
// CLAUDE.md light/dark twins so it reads on both themes.
//
//   <Callout tone="tip" title="Shortcut">Press space to pause.</Callout>
import { ReactNode } from "react";
import { ExclamationTriangleIcon, InformationCircleIcon, LightBulbIcon } from "@heroicons/react/24/outline";
import cn from "classnames";

export type CalloutTone = "tip" | "note" | "warning";

const TONES: Record<CalloutTone, { box: string; icon: string; title: string; Icon: typeof LightBulbIcon; fallbackTitle: string }> = {
  tip: {
    box: "bg-blue-50 dark:bg-blue-900/40 border-blue-100 dark:border-blue-900/60",
    icon: "text-blue-600 dark:text-blue-300",
    title: "text-blue-700 dark:text-blue-200",
    Icon: LightBulbIcon,
    fallbackTitle: "Tip",
  },
  note: {
    box: "bg-base-200 border-base-300",
    icon: "text-base-content/60",
    title: "text-base-content",
    Icon: InformationCircleIcon,
    fallbackTitle: "Note",
  },
  warning: {
    box: "bg-amber-50 dark:bg-amber-950/40 border-amber-200 dark:border-amber-900/60",
    icon: "text-amber-600 dark:text-amber-400",
    title: "text-amber-800 dark:text-amber-200",
    Icon: ExclamationTriangleIcon,
    fallbackTitle: "Heads up",
  },
};

interface Props {
  tone?: CalloutTone;
  title?: string;
  children: ReactNode;
  className?: string;
}

const Callout: React.FC<Props> = ({ tone = "note", title, children, className }) => {
  const t = TONES[tone];
  return (
    <div role="note" className={cn("flex gap-3 rounded-lg border p-3 text-sm", t.box, className)}>
      <t.Icon className={cn("h-5 w-5 shrink-0 mt-0.5", t.icon)} aria-hidden />
      <div className="min-w-0 flex-1">
        <p className={cn("m-0 text-xs font-semibold uppercase tracking-wider", t.title)}>{title ?? t.fallbackTitle}</p>
        <div className="mt-1 text-base-content/80 leading-relaxed [&>p]:m-0">{children}</div>
      </div>
    </div>
  );
};

export default Callout;
